import React, { useEffect, useMemo, useRef, useState } from 'react';
import DeviceTrafficLightAssignments from './DeviceTrafficLightAssignments.jsx';
import { validateTrafficLightAssignments } from './trafficLightValidation.mjs';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

const t = (text) => {
    if (typeof window !== 'undefined' && typeof window.__ === 'function') {
        try {
            return window.__(text);
        } catch (error) {
            return text;
        }
    }

    return text;
};

const normalizeRelayMeta = (value, relayCount = 0) => {
    const source = Array.isArray(value) ? value : [];
    const byId = new Map(source.map((entry) => [Number(entry?.id), entry]));
    return Array.from({ length: relayCount }, (_, index) => {
        const relayId = index + 1;
        const current = byId.get(relayId) || {};
        return {
            id: relayId,
            label: typeof current.label === 'string' ? current.label : '',
            description: typeof current.description === 'string' ? current.description : '',
        };
    });
};

const normalizeRelays = (value) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((entry) => {
            if (!entry || typeof entry !== 'object') {
                return null;
            }

            const index = Number(entry.index ?? entry.channel ?? entry.id);
            if (!Number.isFinite(index) || index <= 0) {
                return null;
            }

            return {
                index,
                tip: ['out', 'in'].includes(String(entry.tip ?? 'out')) ? String(entry.tip ?? 'out') : 'out',
                action: ['open', 'close'].includes(String(entry.action ?? 'open')) ? String(entry.action ?? 'open') : 'open',
                timeout: Number.isFinite(Number(entry.timeout)) && Number(entry.timeout) >= 0 ? Number(Number(entry.timeout).toFixed(2)) : 2,
            };
        })
        .filter(Boolean)
        .sort((left, right) => left.index - right.index);
};

const normalizeAssignments = (value) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((entry) => {
            if (!entry || typeof entry !== 'object') {
                return null;
            }

            const gateId = Number(entry.gate_id ?? entry.gateId ?? entry.id);
            if (!Number.isFinite(gateId) || gateId <= 0) {
                return null;
            }

            return {
                gate_id: gateId,
                relays: normalizeRelays(entry.relays),
            };
        })
        .filter(Boolean);
};

/**
 * Manage gate-to-relay assignments from the device forms.
 */
export default function DeviceGpioAssignments({
    availableGates = [],
    availableTrafficLights = [],
    initialTrafficLightAssignments = [],
    validationErrors = {},
    initialRelayMeta = [],
    initialAssignments = [],
    relayCountInputId = 'input-gpio_relays_count',
    gpioEnabledInputId = 'input-gpio_enabled',
    relayPayloadInputId = 'gpio_relays_payload',
    gateAssignmentsInputId = 'gpio_gate_assignments_payload',
    triggerUrlTemplate = '',
    testDeviceId = '',
    auditContext = null,
    disabled = false,
}) {
    const normalizedInitialAssignments = useMemo(() => normalizeAssignments(initialAssignments), [initialAssignments]);
    const [trafficAssignments, setTrafficAssignments] = useState(() => initialTrafficLightAssignments.map((entry) => ({ ...entry, traffic_light_id: Number(entry.traffic_light_id), relays: entry.relays || [] })));
    const rootRef = useRef(null);
    const [trafficErrors, setTrafficErrors] = useState(() => Object.fromEntries(Object.entries(validationErrors).filter(([field]) => field.startsWith('gpio_traffic_light_assignments'))));
    const focusFirstError = () => {
        const target = rootRef.current?.querySelector('[data-traffic-light-error]');
        target?.focus({ preventScroll: true });
        target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    };
    const updateTrafficAssignments = (update) => {
        setTrafficAssignments(update);
        setTrafficErrors({});
    };
    const [relayCount, setRelayCount] = useState(0);
    useEffect(() => {
        if (Object.keys(trafficErrors).length) focusFirstError();
    }, [trafficErrors, relayCount]);
    const [relayMeta, setRelayMeta] = useState([]);
    const [assignments, setAssignments] = useState(normalizedInitialAssignments);
    const [gpioEnabled, setGpioEnabled] = useState(true);
    const [activeTestKey, setActiveTestKey] = useState('');
    const [testFeedback, setTestFeedback] = useState(null);

    useEffect(() => {
        const input = document.getElementById(relayCountInputId);
        if (!input) {
            return undefined;
        }

        const syncCount = () => {
            const nextCount = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);
            setRelayCount(nextCount);
        };

        syncCount();
        input.addEventListener('input', syncCount);
        input.addEventListener('change', syncCount);

        return () => {
            input.removeEventListener('input', syncCount);
            input.removeEventListener('change', syncCount);
        };
    }, [relayCountInputId]);

    useEffect(() => {
        const input = document.getElementById(gpioEnabledInputId);
        if (!input) {
            setGpioEnabled(true);
            return undefined;
        }

        const syncEnabled = () => {
            const nextEnabled = input.checked === true;
            setGpioEnabled(nextEnabled);

            const relayCountInput = document.getElementById(relayCountInputId);
            if (relayCountInput) {
                relayCountInput.disabled = disabled || !nextEnabled;
            }
        };

        syncEnabled();
        input.addEventListener('input', syncEnabled);
        input.addEventListener('change', syncEnabled);

        return () => {
            input.removeEventListener('input', syncEnabled);
            input.removeEventListener('change', syncEnabled);
        };
    }, [disabled, gpioEnabledInputId, relayCountInputId]);

    useEffect(() => {
        setRelayMeta((current) => normalizeRelayMeta(current.length ? current : initialRelayMeta, relayCount));
    }, [initialRelayMeta, relayCount]);

    useEffect(() => {
        setAssignments(normalizedInitialAssignments.map((assignment) => ({
            ...assignment,
            relays: assignment.relays.filter((relay) => relay.index <= relayCount),
        })));
    }, [normalizedInitialAssignments, relayCount]);

    useEffect(() => {
        const relayInput = document.getElementById(relayPayloadInputId);
        if (relayInput) {
            relayInput.value = JSON.stringify(relayMeta);
        }

        const assignmentsInput = document.getElementById(gateAssignmentsInputId);
        if (assignmentsInput) {
            assignmentsInput.value = JSON.stringify(assignments);
        }
    }, [assignments, gateAssignmentsInputId, relayMeta, relayPayloadInputId]);

    useEffect(() => {
        const input = document.getElementById('gpio_traffic_light_assignments_payload');
        if (input) input.value = JSON.stringify(trafficAssignments);
    }, [trafficAssignments]);

    useEffect(() => {
        const form = rootRef.current?.closest('form');
        if (!form) return undefined;
        const handleSubmit = (event) => {
            if (disabled || !gpioEnabled) return;
            const errors = validateTrafficLightAssignments(trafficAssignments, relayCount, assignments);
            if (!Object.keys(errors).length) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            setTrafficErrors(errors);
        };
        const handleInvalid = (event) => {
            if (!event.target.closest('[data-traffic-light-id]')) return;
            const errors = validateTrafficLightAssignments(trafficAssignments, relayCount, assignments);
            if (Object.keys(errors).length) {
                event.preventDefault();
                setTrafficErrors(errors);
            }
        };
        form.addEventListener('submit', handleSubmit, true);
        form.addEventListener('invalid', handleInvalid, true);
        return () => {
            form.removeEventListener('submit', handleSubmit, true);
            form.removeEventListener('invalid', handleInvalid, true);
        };
    }, [trafficAssignments, assignments, relayCount, gpioEnabled, disabled]);

    const selectedGateIds = useMemo(() => assignments.map((assignment) => assignment.gate_id), [assignments]);
    const selectedGates = useMemo(() => availableGates.filter((gate) => selectedGateIds.includes(Number(gate.id))), [availableGates, selectedGateIds]);
    const relayUsage = useMemo(() => {
        const usage = new Map();
        assignments.forEach((assignment) => {
            assignment.relays.forEach((relay) => usage.set(relay.index, assignment.gate_id));
        });
        trafficAssignments.forEach((assignment) => {
            assignment.relays.forEach((relay) => usage.set(relay.index, `traffic-${assignment.traffic_light_id}`));
        });
        return usage;
    }, [assignments, trafficAssignments]);
    const relayOptions = useMemo(() => relayMeta.map((relay) => ({ ...relay })), [relayMeta]);
    const unusedRelays = useMemo(() => relayOptions.filter((relay) => !relayUsage.has(relay.id)), [relayOptions, relayUsage]);
    const parsedTestDeviceId = Number.parseInt(String(testDeviceId || ''), 10) || 0;
    const canTestRelays = Boolean(triggerUrlTemplate) && parsedTestDeviceId > 0;

    const resolveTriggerUrl = (gateId) => String(triggerUrlTemplate || '').replace('__GATE_ID__', String(gateId));

    const sendRelayTest = async (gateId, gateName, relay, relayConfig) => {
        if (!canTestRelays || relayConfig.tip !== 'out') {
            return;
        }

        const requestKey = `${gateId}-${relay.id}`;
        setActiveTestKey(requestKey);
        setTestFeedback(null);

        try {
            const csrfToken = document.querySelector('input[name="_token"]')?.value
                || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                || '';

            const response = await fetch(resolveTriggerUrl(gateId), {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
                },
                body: JSON.stringify({
                    device_id: parsedTestDeviceId,
                    index: relay.id,
                    relays: [{
                        index: relay.id,
                        tip: relayConfig.tip,
                        action: relayConfig.action,
                        timeout: relayConfig.timeout,
                        label: relay.label?.trim() ? relay.label : `${t('Relay')} ${relay.id}`,
                        description: relay.description || '',
                    }],
                    audit_context: {
                        ...(auditContext && typeof auditContext === 'object' ? auditContext : {}),
                        source: (auditContext && typeof auditContext === 'object' && auditContext.source) ? auditContext.source : 'device',
                    },
                }),
            });

            let payload = {};
            try {
                payload = await response.json();
            } catch (error) {
                payload = {};
            }

            if (!response.ok) {
                throw new Error(payload.message || t('Failed to send relay test command.'));
            }

            setTestFeedback({
                severity: 'success',
                message: payload.message || `${t('Test relay command sent for')} ${gateName}.`,
            });
        } catch (error) {
            setTestFeedback({
                severity: 'error',
                message: error?.message || t('Failed to send relay test command.'),
            });
        } finally {
            setActiveTestKey((current) => (current === requestKey ? '' : current));
        }
    };

    const updateGateSelection = (nextSelectedGates) => {
        const nextIds = nextSelectedGates.map((gate) => Number(gate.id));
        setAssignments((current) => {
            const currentById = new Map(current.map((assignment) => [assignment.gate_id, assignment]));
            return nextIds.map((gateId) => currentById.get(gateId) || { gate_id: gateId, relays: [] });
        });
    };

    const updateAssignmentRelays = (gateId, nextRelays) => {
        setAssignments((current) => current.map((assignment) => (
            assignment.gate_id === gateId ? { ...assignment, relays: normalizeRelays(nextRelays) } : assignment
        )));
    };

    const updateRelayMetaField = (relayId, field, value) => {
        setRelayMeta((current) => current.map((relay) => (
            relay.id === relayId ? { ...relay, [field]: value } : relay
        )));
    };

    const assignmentForGate = (gateId) => assignments.find((assignment) => assignment.gate_id === gateId) || { gate_id: gateId, relays: [] };
    const controlsDisabled = disabled || !gpioEnabled;

    return (
        <Stack ref={rootRef} spacing={2}>
            {!gpioEnabled ? (
                <Alert severity="info">{t('Enable GPIO on the device to edit gate and relay assignments.')}</Alert>
            ) : relayCount <= 0 ? (
                <Alert severity="info">{t('Add at least one relay on the device before assigning gates.')}</Alert>
            ) : (
                <>
                    {testFeedback && (
                        <Alert severity={testFeedback.severity} onClose={() => setTestFeedback(null)}>
                            {testFeedback.message}
                        </Alert>
                    )}

                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('Gates on this device')}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('Select gates and then choose which relay indexes belong to each gate. Free relay indexes can stay unused.')}
                            </Typography>
                            <Autocomplete
                                multiple
                                disabled={controlsDisabled}
                                options={availableGates}
                                value={selectedGates}
                                getOptionLabel={(option) => option?.name || ''}
                                onChange={(event, value) => updateGateSelection(value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('Selected gates')}
                                        helperText={t('Available list includes gates not yet assigned and gates already attached to this device.')}
                                    />
                                )}
                            />
                        </CardContent>
                    </Card>

                    {selectedGates.map((gate) => {
                        const assignment = assignmentForGate(Number(gate.id));
                        return (
                            <Card key={gate.id} variant="outlined">
                                <CardContent>
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography variant="subtitle1">{gate.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {`${gate.type || 'gate'}${gate.description ? ` · ${gate.description}` : ''}`}
                                            </Typography>
                                        </Box>

                                        <Grid container spacing={1}>
                                            {relayOptions.map((relay) => {
                                                const selected = assignment.relays.some((entry) => entry.index === relay.id);
                                                const ownerGateId = relayUsage.get(relay.id);
                                                const disabledByOtherGate = ownerGateId !== undefined && ownerGateId !== Number(gate.id);
                                                const relayConfig = assignment.relays.find((entry) => entry.index === relay.id) || null;
                                                const testKey = `${gate.id}-${relay.id}`;

                                                return (
                                                    <Grid item xs={12} md={6} key={`${gate.id}-${relay.id}`}>
                                                        <Card variant="outlined" sx={{ opacity: disabledByOtherGate ? 0.55 : 1 }}>
                                                            <CardContent>
                                                                <Stack spacing={1.5}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                disabled={controlsDisabled || disabledByOtherGate}
                                                                                checked={selected}
                                                                                onChange={(event) => {
                                                                                    const checked = event.target.checked;
                                                                                    const existing = normalizeRelays(assignment.relays);
                                                                                    const nextRelays = checked
                                                                                        ? [...existing, { index: relay.id, tip: 'out', action: 'open', timeout: 2 }]
                                                                                        : existing.filter((entry) => entry.index !== relay.id);
                                                                                    updateAssignmentRelays(Number(gate.id), nextRelays);
                                                                                }}
                                                                            />
                                                                        }
                                                                        label={relay.label?.trim() ? relay.label : `${t('Relay')} ${relay.id}`}
                                                                    />

                                                                    {selected && (
                                                                        <>
                                                                            <TextField
                                                                                size="small"
                                                                                label={t('Label')}
                                                                                value={relay.label}
                                                                                onChange={(event) => updateRelayMetaField(relay.id, 'label', event.target.value)}
                                                                                disabled={controlsDisabled}
                                                                            />
                                                                            <TextField
                                                                                size="small"
                                                                                label={t('Description')}
                                                                                value={relay.description}
                                                                                onChange={(event) => updateRelayMetaField(relay.id, 'description', event.target.value)}
                                                                                disabled={controlsDisabled}
                                                                            />
                                                                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                                                                                <TextField
                                                                                    select
                                                                                    size="small"
                                                                                    label={t('Tip')}
                                                                                    value={relayConfig?.tip ?? 'out'}
                                                                                    onChange={(event) => updateAssignmentRelays(Number(gate.id), assignment.relays.map((entry) => entry.index === relay.id ? { ...entry, tip: event.target.value } : entry))}
                                                                                    disabled={controlsDisabled}
                                                                                >
                                                                                    <MenuItem value="out">out</MenuItem>
                                                                                    <MenuItem value="in">in</MenuItem>
                                                                                </TextField>
                                                                                <TextField
                                                                                    select
                                                                                    size="small"
                                                                                    label={t('Action')}
                                                                                    value={relayConfig?.action ?? 'open'}
                                                                                    onChange={(event) => updateAssignmentRelays(Number(gate.id), assignment.relays.map((entry) => entry.index === relay.id ? { ...entry, action: event.target.value } : entry))}
                                                                                    disabled={controlsDisabled}
                                                                                >
                                                                                    <MenuItem value="open">open</MenuItem>
                                                                                    <MenuItem value="close">close</MenuItem>
                                                                                </TextField>
                                                                                <TextField
                                                                                    size="small"
                                                                                    type="number"
                                                                                    label={t('Timeout')}
                                                                                    inputProps={{ min: 0, step: 0.1 }}
                                                                                    value={relayConfig?.timeout ?? 2}
                                                                                    onChange={(event) => {
                                                                                        const nextTimeout = Number(event.target.value);
                                                                                        updateAssignmentRelays(Number(gate.id), assignment.relays.map((entry) => (
                                                                                            entry.index === relay.id
                                                                                                ? { ...entry, timeout: Number.isFinite(nextTimeout) && nextTimeout >= 0 ? Number(nextTimeout.toFixed(2)) : 0 }
                                                                                                : entry
                                                                                        )));
                                                                                    }}
                                                                                    disabled={controlsDisabled}
                                                                                />
                                                                            </Stack>

                                                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', minHeight: 36 }}>
                                                                                {relayConfig?.tip !== 'out' ? (
                                                                                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                                                                        {t('Manual relay testing is available only for output relays.')}
                                                                                    </Typography>
                                                                                ) : canTestRelays ? (
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        onClick={() => sendRelayTest(Number(gate.id), gate.name, relay, relayConfig)}
                                                                                        disabled={controlsDisabled || activeTestKey === testKey}
                                                                                    >
                                                                                        {activeTestKey === testKey ? t('Sending...') : t('Test relay')}
                                                                                    </Button>
                                                                                ) : null}
                                                                            </Box>
                                                                        </>
                                                                    )}
                                                                    {!selected && disabledByOtherGate && (
                                                                        <Typography variant="caption" color="text.secondary">{t('Already used by a gate or traffic light on this device.')}</Typography>
                                                                    )}
                                                                </Stack>
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Stack>
                                </CardContent>
                            </Card>
                        );
                    })}

                    <Box component="section" sx={{ borderTop: 1, borderColor: 'divider', pt: 3 }}>
                        <DeviceTrafficLightAssignments availableLights={availableTrafficLights}
                            assignments={trafficAssignments} setAssignments={updateTrafficAssignments}
                            errors={trafficErrors} onFocusError={focusFirstError}
                            relayOptions={relayOptions} relayUsage={relayUsage} disabled={controlsDisabled} />
                    </Box>

                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle2">{t('Free relays')}</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                {unusedRelays.length > 0
                                    ? unusedRelays.map((relay) => <Chip key={`free-${relay.id}`} size="small" label={relay.label?.trim() ? `${relay.label} (#${relay.id})` : `${t('Relay')} ${relay.id}`} />)
                                    : <Typography variant="caption" color="text.secondary">{t('All relay indexes are currently assigned.')}</Typography>}
                            </Stack>
                        </CardContent>
                    </Card>
                </>
            )}
        </Stack>
    );
}